/**
 * Language switcher script
 * Handles language menu toggle interactions
 */

import { BREAKPOINTS } from '../variables.ts';

((Drupal, once) => {
  'use strict';

  /**
   * Drupal behavior for language switcher
   */
  Drupal.behaviors.tpLanguageSwitcher = {
    attach(context: Document | HTMLElement) {
      const languageSwitchers = once('tp-language-switcher', '.menu--language', context);

      languageSwitchers.forEach((switcher) => {
        const container = switcher as HTMLElement;
        const button = container.querySelector<HTMLButtonElement>('.links__button');
        const submenu = container.querySelector<HTMLElement>('.submenu');

        if (!button || !submenu) {
          return;
        }

        const setExpanded = (expanded: boolean) => {
          container.setAttribute('data-expanded', expanded ? 'true' : 'false');
          button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          submenu.setAttribute('aria-hidden', expanded ? 'false' : 'true');

          // Update SVG rotation via data attribute
          const svg = button.querySelector('svg');
          if (svg) {
            svg.setAttribute('data-expanded', expanded ? 'true' : 'false');
          }
        };

        // Ensure initial state
        setExpanded(container.getAttribute('data-expanded') === 'true');

        // Toggle on button click
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const isExpanded = container.getAttribute('data-expanded') === 'true';
          setExpanded(!isExpanded);
        });

        // Open/close on hover (mouse users) - only on desktop
        const isMobile = () => window.innerWidth <= BREAKPOINTS.md;

        const parentContainer = container.parentElement;
        if (parentContainer) {
          parentContainer.addEventListener('mouseenter', () => {
            if (!isMobile()) {
              setExpanded(true);
            }
          });

          parentContainer.addEventListener('mouseleave', () => {
            if (!isMobile()) {
              setExpanded(false);
            }
          });
        }

        // Keyboard accessibility
        button.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const isExpanded = container.getAttribute('data-expanded') === 'true';
            setExpanded(!isExpanded);
          }
          if (event.key === 'Escape') {
            setExpanded(false);
            button.focus();
          }
        });

        // Close submenu when clicking outside
        document.addEventListener('click', (event) => {
          const target = event.target as HTMLElement;
          if (!container.contains(target) && !submenu.contains(target)) {
            setExpanded(false);
          }
        });

        // Close submenu when a language link is clicked
        const languageLinks = submenu.querySelectorAll('a');
        languageLinks.forEach((link) => {
          link.addEventListener('click', () => {
            setExpanded(false);
          });
        });
      });
    },
  };
})(Drupal, once);
